import { useState, useEffect } from 'react';
import ActorChip from '../components/audit/ActorChip';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  settlementsApi,
  CreditNoteResponse,
  SettlementDetailResponse,
  TaxInvoiceUrlResponse,
} from '../services/settlementsApi';

const MONEY_STYLE = { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' };
/** Spec 053 (#470, AC-9) — muted "− reversed …" sub-line under a line's commission / VAT. */
const REVERSED_STYLE = { fontSize: '0.75rem', color: 'var(--admin-text-muted)', whiteSpace: 'nowrap' as const };

/** Local calendar date as YYYY-MM-DD (the transfer date defaults to today, in the admin's zone). */
function todayIsoDate(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface PayFieldErrors {
  reference?: string;
  transferDate?: string;
  file?: string;
}

export default function SettlementDetailPage() {
  const { settlementId } = useParams<{ settlementId: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [settlement, setSettlement] = useState<SettlementDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taxInvoice, setTaxInvoice] = useState<TaxInvoiceUrlResponse | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  // Spec 053 Flow A2 — CCNs issued against this settlement's INV- (refund after invoice).
  const [creditNotes, setCreditNotes] = useState<CreditNoteResponse[]>([]);
  const [creditNotesError, setCreditNotesError] = useState<string | null>(null);
  // Per-CCN PDF state learned from the url endpoint: GENERATING keeps the button disabled.
  const [ccnGenerating, setCcnGenerating] = useState<Record<string, boolean>>({});
  const [ccnBusy, setCcnBusy] = useState<string | null>(null);

  // Record-payment dialog (Spec 053 Flow A1 / #469): one form, every field but bank name required.
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [reference, setReference] = useState('');
  const [bankName, setBankName] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [fieldErrors, setFieldErrors] = useState<PayFieldErrors>({});
  const [payError, setPayError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const runAction = async (fn: () => Promise<unknown>) => {
    if (!settlementId || !auth.token) return;
    setActionError(null);
    setActionBusy(true);
    try {
      await fn();
      fetchDetail();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const handleApprove = () => runAction(() => settlementsApi.approve(settlementId!, auth.token!));
  const handleRelease = () => runAction(() => settlementsApi.release(settlementId!, auth.token!));
  const handleVoid = () => {
    const reason = window.prompt('Type a reason to VOID this settlement (orders return to the pool):');
    if (!reason) return;
    runAction(() => settlementsApi.voidSettlement(settlementId!, reason, auth.token!));
  };
  const handleAddAdjustment = () => {
    const type = window.prompt('Adjustment type — CREDIT or DEBIT:', 'CREDIT');
    if (type !== 'CREDIT' && type !== 'DEBIT') return;
    const amountStr = window.prompt('Amount (AED):', '0');
    const amount = Number(amountStr);
    if (!amount || amount <= 0) return;
    const reason = window.prompt('Reason for this adjustment:');
    if (!reason) return;
    runAction(() => settlementsApi.addAdjustment(settlementId!, type, amount, reason, auth.token!));
  };

  const fetchDetail = () => {
    if (!settlementId || !auth.token) return;
    setLoading(true);
    settlementsApi.getSettlementDetail(settlementId, auth.token)
      .then(setSettlement)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDetail(); }, [settlementId, auth.token]);

  // The commission invoice only exists once PAID (Flow A1); ask for its state then.
  useEffect(() => {
    if (!settlementId || !auth.token || settlement?.status !== 'PAID') {
      setTaxInvoice(null);
      return;
    }
    settlementsApi.getTaxInvoiceUrl(settlementId, auth.token)
      .then(setTaxInvoice)
      .catch((err) => console.error('Failed to load tax invoice state:', err));
  }, [settlementId, auth.token, settlement?.status]);

  // Credit notes only exist once an INV- does (AWAITING_PAYMENT never has one, PAID may) — but the
  // Documents card renders for both, so fetch for both and let the empty state speak.
  useEffect(() => {
    const status = settlement?.status;
    if (!settlementId || !auth.token || (status !== 'AWAITING_PAYMENT' && status !== 'PAID')) {
      setCreditNotes([]);
      setCreditNotesError(null);
      return;
    }
    settlementsApi.listCreditNotes(settlementId, auth.token)
      .then((notes) => {
        setCreditNotes(notes);
        setCreditNotesError(null);
        setCcnGenerating(Object.fromEntries(notes.map((n) => [n.ccnId, n.status === 'PENDING_DOCUMENT'])));
      })
      .catch((err) => {
        console.error('Failed to load credit notes:', err);
        setCreditNotesError('Could not load credit notes');
      });
  }, [settlementId, auth.token, settlement?.status]);

  const handleDownloadCreditNote = async (ccnId: string) => {
    if (!auth.token) return;
    setCcnBusy(ccnId);
    try {
      const fresh = await settlementsApi.getCreditNoteUrl(ccnId, auth.token);
      setCcnGenerating((prev) => ({ ...prev, [ccnId]: fresh.status === 'GENERATING' }));
      if (fresh.status === 'AVAILABLE' && fresh.downloadUrl) window.open(fresh.downloadUrl, '_blank');
    } catch (err) {
      console.error('Failed to get credit note URL:', err);
    } finally {
      setCcnBusy(null);
    }
  };

  const openPayDialog = () => {
    if (!settlement) return;
    setReference('');
    setBankName('');
    setFile(null);
    setTransferDate(todayIsoDate());
    setTransferAmount(settlement.netPayable.toFixed(2));
    setFieldErrors({});
    setPayError(null);
    setShowPayDialog(true);
  };

  const closePayDialog = () => {
    if (submitting) return;
    setShowPayDialog(false);
  };

  const validatePayForm = (): PayFieldErrors => {
    const errors: PayFieldErrors = {};
    if (!reference.trim()) errors.reference = 'Bank transfer reference is required';
    if (!transferDate) errors.transferDate = 'Transfer date is required';
    if (!file) errors.file = 'Transfer proof is required';
    return errors;
  };

  const handleMarkPaid = async () => {
    if (!settlementId || !auth.token || !settlement) return;
    setPayError(null);
    const errors = validatePayForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const formData = new FormData();
    formData.append('reference', reference.trim());
    formData.append('transferAmount', settlement.netPayable.toFixed(2));
    formData.append('transferDate', transferDate);
    if (bankName.trim()) formData.append('bankName', bankName.trim());
    formData.append('confirmationFile', file!);

    setSubmitting(true);
    try {
      await settlementsApi.markSettlementPaid(settlementId, formData, auth.token);
      setShowPayDialog(false);
      fetchDetail();
    } catch (err) {
      setPayError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadConfirmation = async () => {
    if (!settlementId || !auth.token) return;
    try {
      const { url } = await settlementsApi.getConfirmationUrl(settlementId, auth.token);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to get confirmation URL:', err);
    }
  };

  // Presigned URLs expire, so fetch a fresh one on click rather than reusing the load-time value.
  const handleDownloadInvoice = async () => {
    if (!settlementId || !auth.token) return;
    setDownloadingInvoice(true);
    try {
      const fresh = await settlementsApi.getTaxInvoiceUrl(settlementId, auth.token);
      setTaxInvoice(fresh);
      if (fresh.downloadUrl) window.open(fresh.downloadUrl, '_blank');
    } catch (err) {
      console.error('Failed to get tax invoice URL:', err);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p style={{ marginTop: '0.75rem', color: 'var(--admin-text-muted)' }}>Loading settlement...</p>
      </div>
    );
  }

  if (error) return <div className="admin-info-banner danger">{error}</div>;
  if (!settlement) return <div className="admin-info-banner danger">Settlement not found</div>;

  const carriedIn = settlement.carriedBalanceIn ?? 0;
  const carriedOut = settlement.carriedBalanceOut ?? 0;
  // Spec 067 — Tabby fees the garage bears (+ VAT) are deductions; adjustments are signed (credits +).
  const tabbyFees = settlement.gatewayFeeDeductions ?? 0;
  const tabbyFeeVat = settlement.gatewayFeeVat ?? 0;
  const adjustments = settlement.adjustmentsTotal ?? 0;
  const showLineTabbyFee = settlement.lineItems.some(
    (item) => (item.garageFeeDeduction ?? 0) !== 0 || (item.garageFeeVat ?? 0) !== 0);
  // Spec 068 V16 — service fees recharged to the garage after a GARAGE-fault refund; hidden when 0/null.
  const serviceFeeRecharges = settlement.serviceFeeDeductions ?? 0;
  const serviceFeeRechargesVat = settlement.serviceFeeDeductionsVat ?? 0;
  const showServiceFeeRecharges = serviceFeeRecharges > 0 || serviceFeeRechargesVat > 0;
  const showLineServiceFee = settlement.lineItems.some(
    (item) => (item.serviceFeeDeduction ?? 0) > 0 || (item.serviceFeeDeductionVat ?? 0) > 0);
  const invoiceNumber = settlement.taxInvoiceNumber ?? taxInvoice?.invoiceNumber ?? null;
  const showDocuments = settlement.status === 'AWAITING_PAYMENT' || settlement.status === 'PAID';
  const confirmation = settlement.paymentConfirmation;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
        <button onClick={() => navigate('/settlements')} className="admin-btn admin-btn-outline admin-btn-sm">
          Back
        </button>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>
          Settlement {settlement.settlementNumber}
        </h2>
        <span className={`admin-badge ${settlement.status === 'PAID' ? 'approved' : settlement.status === 'VOID' ? 'rejected' : settlement.status === 'ON_HOLD' ? 'warning' : 'pending'}`}>
          {settlement.status}
        </span>
      </div>

      <div className="admin-card" style={{ marginBottom: '1rem' }}>
        <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Settlement Details</h3>
        <div className="admin-detail-grid">
          <div className="admin-field">
            <div className="admin-field-label">Garage</div>
            <div className="admin-field-value">{settlement.garageName}</div>
          </div>
          <div className="admin-field">
            <div className="admin-field-label">Period</div>
            <div className="admin-field-value">{settlement.periodStart} to {settlement.periodEnd}</div>
          </div>
          <div className="admin-field">
            <div className="admin-field-label">Orders</div>
            <div className="admin-field-value">{settlement.orderCount}</div>
          </div>
          {settlement.approvedByName?.trim() && (
            <div className="admin-field">
              <div className="admin-field-label">Approved by</div>
              <div className="admin-field-value" data-testid="approved-by">
                <ActorChip role="ADMIN" name={settlement.approvedByName} link={false} />
              </div>
            </div>
          )}
          {settlement.settledByUserId && (
            <div className="admin-field">
              <div className="admin-field-label">Settled by</div>
              <div className="admin-field-value" data-testid="settled-by">
                <ActorChip role="ADMIN" name={settlement.settledByName} subjectId={settlement.settledByUserId} />
                {settlement.settledAt && <span className="audit-cell--muted"> on {new Date(settlement.settledAt).toLocaleString()}</span>}
              </div>
            </div>
          )}
          {carriedIn !== 0 && (
            <div className="admin-field">
              <div className="admin-field-label">Carried in from previous cycle</div>
              <div className="admin-field-value">{carriedIn.toFixed(2)} {settlement.currency}</div>
            </div>
          )}
          {carriedOut !== 0 && (
            <div className="admin-field">
              <div className="admin-field-label">Carried forward to next cycle</div>
              <div className="admin-field-value">{carriedOut.toFixed(2)} {settlement.currency}</div>
            </div>
          )}
        </div>
      </div>

      {/* Financial Breakdown */}
      <div className="admin-stats-row" style={{ marginBottom: '1rem' }}>
        <div className="admin-stat-card">
          <div className="admin-stat-icon primary">AED</div>
          <div>
            <div className="admin-stat-value">{settlement.grossRevenue.toFixed(2)}</div>
            <div className="admin-stat-label">Gross Revenue</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon warning">%</div>
          <div>
            <div className="admin-stat-value">-{settlement.commissionTotal.toFixed(2)}</div>
            <div className="admin-stat-label">Commission</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon info">VAT</div>
          <div>
            <div className="admin-stat-value">-{settlement.vatOnCommission.toFixed(2)}</div>
            <div className="admin-stat-label">VAT on Commission</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon danger">-</div>
          <div>
            <div className="admin-stat-value">-{settlement.refundDeductions.toFixed(2)}</div>
            <div className="admin-stat-label">Refund Deductions</div>
          </div>
        </div>
        {(tabbyFees !== 0 || tabbyFeeVat !== 0) && (
          <>
            <div className="admin-stat-card">
              <div className="admin-stat-icon danger">-</div>
              <div>
                <div className="admin-stat-value">-{tabbyFees.toFixed(2)}</div>
                <div className="admin-stat-label">Tabby fees</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-icon info">VAT</div>
              <div>
                <div className="admin-stat-value">-{tabbyFeeVat.toFixed(2)}</div>
                <div className="admin-stat-label">VAT on Tabby fees</div>
              </div>
            </div>
          </>
        )}
        {showServiceFeeRecharges && (
          <>
            <div className="admin-stat-card" data-testid="stat-service-fee-recharges">
              <div className="admin-stat-icon danger">-</div>
              <div>
                <div className="admin-stat-value">-{serviceFeeRecharges.toFixed(2)}</div>
                <div className="admin-stat-label">Service fee recharges</div>
              </div>
            </div>
            <div className="admin-stat-card" data-testid="stat-service-fee-recharges-vat">
              <div className="admin-stat-icon info">VAT</div>
              <div>
                <div className="admin-stat-value">-{serviceFeeRechargesVat.toFixed(2)}</div>
                <div className="admin-stat-label">VAT on service fee recharges</div>
              </div>
            </div>
          </>
        )}
        <div className="admin-stat-card">
          <div className="admin-stat-icon primary">±</div>
          <div>
            <div className="admin-stat-value">{adjustments > 0 ? '+' : ''}{adjustments.toFixed(2)}</div>
            <div className="admin-stat-label">Adjustments</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon success">NET</div>
          <div>
            <div className="admin-stat-value" style={{ fontWeight: 700 }}>{settlement.netPayable.toFixed(2)}</div>
            <div className="admin-stat-label">Net Payable ({settlement.currency})</div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {settlement.status === 'DRAFT' && (
          <>
            <button className="admin-btn admin-btn-primary" onClick={handleApprove} disabled={actionBusy}>
              Approve for payment
            </button>
            <button className="admin-btn admin-btn-outline" onClick={handleAddAdjustment} disabled={actionBusy}>
              Add adjustment
            </button>
            <button className="admin-btn admin-btn-outline" onClick={handleVoid} disabled={actionBusy}>
              Void
            </button>
          </>
        )}
        {settlement.status === 'AWAITING_PAYMENT' && (
          <>
            <button className="admin-btn admin-btn-primary" onClick={openPayDialog} disabled={actionBusy}>
              Record payment
            </button>
            <button className="admin-btn admin-btn-outline" onClick={handleVoid} disabled={actionBusy}>
              Void
            </button>
          </>
        )}
        {settlement.status === 'ON_HOLD' && (
          <button className="admin-btn admin-btn-primary" onClick={handleRelease} disabled={actionBusy}>
            Release hold
          </button>
        )}
        {actionError && <span style={{ color: '#dc2626', fontSize: '0.875rem', alignSelf: 'center' }}>{actionError}</span>}
      </div>

      {/* Documents — commission tax invoice (Spec 053 Flow A1: issued when payment is recorded) */}
      {showDocuments && (
        <div className="admin-card" style={{ marginBottom: '1rem' }}>
          <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Documents</h3>
          {settlement.status === 'AWAITING_PAYMENT' && !invoiceNumber ? (
            <p style={{ color: 'var(--admin-text-muted)', fontSize: '0.875rem', margin: 0 }}>
              Commission invoice is issued when payment is recorded
            </p>
          ) : (
            <div className="admin-detail-grid">
              <div className="admin-field">
                <div className="admin-field-label">Commission tax invoice</div>
                <div className="admin-field-value">{invoiceNumber ?? '—'}</div>
                {settlement.taxInvoiceIssuedAt && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                    Issued {new Date(settlement.taxInvoiceIssuedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
              {settlement.status === 'PAID' && taxInvoice && taxInvoice.status !== 'NOT_AVAILABLE' && (
                <div className="admin-field">
                  <div className="admin-field-label">PDF</div>
                  {taxInvoice.status === 'AVAILABLE' ? (
                    <button
                      className="admin-btn admin-btn-outline admin-btn-sm"
                      onClick={handleDownloadInvoice}
                      disabled={downloadingInvoice}
                    >
                      {downloadingInvoice ? 'Preparing…' : 'Download tax invoice'}
                    </button>
                  ) : (
                    <button className="admin-btn admin-btn-outline admin-btn-sm" disabled>
                      Generating…
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Credit notes — Spec 053 Flow A2: CCN- issued against the INV- for refunds after invoicing */}
          <div style={{ marginTop: '1.25rem' }}>
            <div className="admin-field-label" style={{ marginBottom: '0.5rem' }}>Credit notes</div>
            {creditNotesError ? (
              <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>{creditNotesError}</p>
            ) : creditNotes.length === 0 ? (
              <p style={{ color: 'var(--admin-text-muted)', fontSize: '0.875rem', margin: 0 }}>No credit notes</p>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table" aria-label="Credit notes">
                  <thead>
                    <tr>
                      <th>Number</th>
                      <th>Issued</th>
                      <th style={{ textAlign: 'right' }}>Net</th>
                      <th style={{ textAlign: 'right' }}>VAT</th>
                      <th style={{ textAlign: 'right' }}>Gross</th>
                      <th>Against</th>
                      <th>References</th>
                      <th>PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditNotes.map((ccn) => {
                      const generating = ccnGenerating[ccn.ccnId] ?? ccn.status === 'PENDING_DOCUMENT';
                      const busy = ccnBusy === ccn.ccnId;
                      return (
                        <tr key={ccn.ccnId}>
                          <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {ccn.number}
                            {ccn.capped && (
                              <span
                                className="admin-badge pending"
                                style={{ marginLeft: '0.5rem' }}
                                title="Credit was capped at the remaining invoice balance"
                              >
                                Capped
                              </span>
                            )}
                          </td>
                          <td>{new Date(ccn.issuedAt).toLocaleDateString()}</td>
                          <td style={MONEY_STYLE}>{ccn.net.toFixed(2)}</td>
                          <td style={MONEY_STYLE}>{ccn.vat.toFixed(2)}</td>
                          <td style={{ ...MONEY_STYLE, fontWeight: 600 }}>{ccn.gross.toFixed(2)}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>against {ccn.originalInvoiceNumber}</td>
                          <td style={{ fontSize: '0.8rem' }}>
                            {ccn.refOrderId ? (
                              <button
                                type="button"
                                className="admin-btn admin-btn-outline admin-btn-sm"
                                onClick={() => navigate(`/orders/${ccn.refOrderId}`)}
                              >
                                Order {ccn.refOrderNumber ?? ccn.refOrderId.substring(0, 8)}
                              </button>
                            ) : null}
                            {ccn.refundId && (
                              <div style={{ fontFamily: 'monospace', color: 'var(--admin-text-muted)', marginTop: ccn.refOrderId ? '0.25rem' : 0 }}>
                                Refund {ccn.refundId}
                              </div>
                            )}
                            {!ccn.refOrderId && !ccn.refundId && '—'}
                          </td>
                          <td>
                            {generating ? (
                              <button className="admin-btn admin-btn-outline admin-btn-sm" disabled>
                                Preparing…
                              </button>
                            ) : (
                              <button
                                className="admin-btn admin-btn-outline admin-btn-sm"
                                onClick={() => handleDownloadCreditNote(ccn.ccnId)}
                                disabled={busy}
                              >
                                {busy ? 'Preparing…' : `Download ${ccn.number}`}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {settlement.status === 'PAID' && confirmation && (
        <div className="admin-card" style={{ marginBottom: '1rem' }}>
          <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Payment Confirmation</h3>
          <div className="admin-detail-grid">
            {confirmation.reference && (
              <div className="admin-field">
                <div className="admin-field-label">Reference</div>
                <div className="admin-field-value">{confirmation.reference}</div>
              </div>
            )}
            {confirmation.bankName && (
              <div className="admin-field">
                <div className="admin-field-label">Bank</div>
                <div className="admin-field-value">{confirmation.bankName}</div>
              </div>
            )}
            {confirmation.transferDate && (
              <div className="admin-field">
                <div className="admin-field-label">Transfer Date</div>
                <div className="admin-field-value">{confirmation.transferDate}</div>
              </div>
            )}
            {confirmation.transferAmount != null && (
              <div className="admin-field">
                <div className="admin-field-label">Amount</div>
                <div className="admin-field-value">{confirmation.transferAmount.toFixed(2)} {settlement.currency}</div>
              </div>
            )}
            {(confirmation.documentKey || confirmation.type === 'DOCUMENT') && (
              <div className="admin-field">
                <div className="admin-field-label">Transfer proof</div>
                <button onClick={handleDownloadConfirmation} className="admin-btn admin-btn-outline admin-btn-sm">
                  Download proof
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Breakdown */}
      <div className="admin-card">
        <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Order Breakdown</h3>
        {settlement.lineItems.length === 0 ? (
          <div className="admin-empty-state"><p>No line items</p></div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th style={{ textAlign: 'right' }}>Gross</th>
                  <th style={{ textAlign: 'right' }}>Commission</th>
                  <th style={{ textAlign: 'right' }}>VAT</th>
                  <th style={{ textAlign: 'right' }}>Net</th>
                  {showLineTabbyFee && <th style={{ textAlign: 'right' }}>Tabby fee</th>}
                  {showLineServiceFee && <th style={{ textAlign: 'right' }}>Service fee recharge</th>}
                  <th>Refunded</th>
                </tr>
              </thead>
              <tbody>
                {settlement.lineItems.map((item) => (
                  <tr key={item.orderId} style={item.isRefunded ? { opacity: 0.6 } : undefined}>
                    <td style={{ fontWeight: 500 }}>{item.orderId.substring(0, 8)}...</td>
                    <td>{item.orderDate ? new Date(item.orderDate).toLocaleDateString() : '-'}</td>
                    <td>{item.customerName || '-'}</td>
                    <td style={{ textAlign: 'right' }}>{item.grossAmount.toFixed(2)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {item.commissionAmount?.toFixed(2) || '-'}
                      {item.reversedCommission > 0 && (
                        <div style={REVERSED_STYLE}>− reversed {item.reversedCommission.toFixed(2)}</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {item.vatOnCommission?.toFixed(2) || '-'}
                      {item.reversedVat > 0 && (
                        <div style={REVERSED_STYLE}>− reversed {item.reversedVat.toFixed(2)}</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{item.netAmount?.toFixed(2) || '-'}</td>
                    {showLineTabbyFee && (
                      <td style={{ textAlign: 'right' }}>
                        {(item.garageFeeDeduction ?? 0) !== 0 ? `-${(item.garageFeeDeduction ?? 0).toFixed(2)}` : '-'}
                        {(item.garageFeeVat ?? 0) !== 0 && (
                          <div style={REVERSED_STYLE}>VAT -{(item.garageFeeVat ?? 0).toFixed(2)}</div>
                        )}
                      </td>
                    )}
                    {showLineServiceFee && (
                      <td style={{ textAlign: 'right' }} data-testid="line-service-fee-recharge">
                        {(item.serviceFeeDeduction ?? 0) > 0 ? `-${(item.serviceFeeDeduction as number).toFixed(2)}` : '-'}
                        {(item.serviceFeeDeductionVat ?? 0) > 0 && (
                          <div style={REVERSED_STYLE}>VAT -{(item.serviceFeeDeductionVat as number).toFixed(2)}</div>
                        )}
                      </td>
                    )}
                    <td>
                      {item.isRefunded ? (
                        <span className="admin-badge rejected">Yes (-{item.refundAmount?.toFixed(2)})</span>
                      ) : (
                        <span style={{ color: 'var(--admin-text-muted)' }}>No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Record Payment Dialog — Spec 053 Flow A1: reference + amount (= net payable) + proof all required */}
      {showPayDialog && (
        <div className="admin-modal-overlay" onClick={closePayDialog}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="record-payment-title">
            <div className="admin-modal-header">
              <h3 id="record-payment-title">Record payment</h3>
              <button className="admin-modal-close" onClick={closePayDialog} aria-label="Close">&times;</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-info-banner info" style={{ marginBottom: '1rem' }}>
                Recording the payment marks this settlement PAID and issues the commission tax invoice.
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="pay-reference">Bank transfer reference *</label>
                <input
                  id="pay-reference"
                  type="text"
                  className="admin-form-input"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  disabled={submitting}
                  aria-invalid={!!fieldErrors.reference}
                />
                {fieldErrors.reference && <span style={{ color: "#dc2626", fontSize: "0.75rem", display: "block", marginTop: "0.25rem" }}>{fieldErrors.reference}</span>}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="pay-transfer-date">Transfer date *</label>
                <input
                  id="pay-transfer-date"
                  type="date"
                  className="admin-form-input"
                  value={transferDate}
                  onChange={(e) => setTransferDate(e.target.value)}
                  disabled={submitting}
                  aria-invalid={!!fieldErrors.transferDate}
                />
                {fieldErrors.transferDate && <span style={{ color: "#dc2626", fontSize: "0.75rem", display: "block", marginTop: "0.25rem" }}>{fieldErrors.transferDate}</span>}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="pay-transfer-amount">Transfer amount ({settlement.currency}) *</label>
                <input
                  id="pay-transfer-amount"
                  type="text"
                  inputMode="decimal"
                  className="admin-form-input"
                  value={transferAmount}
                  readOnly
                  aria-readonly="true"
                />
                <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem', display: 'block' }}>
                  Must match net payable
                </span>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="pay-bank-name">Bank name</label>
                <input
                  id="pay-bank-name"
                  type="text"
                  className="admin-form-input"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="pay-proof">Transfer proof (PDF, PNG, JPG, max 10MB) *</label>
                <input
                  id="pay-proof"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  disabled={submitting}
                  aria-invalid={!!fieldErrors.file}
                />
                {fieldErrors.file && <span style={{ color: "#dc2626", fontSize: "0.75rem", display: "block", marginTop: "0.25rem" }}>{fieldErrors.file}</span>}
              </div>

              {payError && <div className="admin-info-banner danger" role="alert">{payError}</div>}
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-outline" onClick={closePayDialog} disabled={submitting}>
                Cancel
              </button>
              <button className="admin-btn admin-btn-primary" onClick={handleMarkPaid} disabled={submitting}>
                {submitting ? 'Recording...' : 'Record payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
